import React, { useState, useMemo, useEffect } from 'react';
import { Paper, TableHead, TableContainer, Dialog, DialogTitle, DialogContent, TextField, Button, Grid, Typography, Divider, Box, Table, TableBody, TableRow, TableCell, CircularProgress } from '@mui/material';
import styles from './Logbook.module.scss';
import { Edit, Delete, Visibility } from '@mui/icons-material';
import LogbookWordIcon from "../../assets/images/LogbookWordIcons.png"
import { deleteShiftNote, deleteEmpNote, postWorkplaceDailySummary, putEmpNote, putShiftNote, getWorkplaceDailySummary, getBroadcastMessages, getShiftNotes, getEmpNotes, postEmpNote, postShiftNote, getDayParts, getDayPartsUnionMetrics } from 'core/API/logbook';
import { getEnrichedWorkplaceUsers } from 'core/utils/enrichWorkplaceUsers';
import { getChecklistOutstanding } from 'core/API/checklist';
import { fetchAllEvents, fetchAllEventTypes } from 'core/API/events';
import moment from 'moment';

import { useQuery } from 'react-query';
import {useDocumentContext} from 'module/documents/context';
import Select from 'react-select';
import { useQueryClient } from 'react-query'; 
import { useToastContext } from 'core/components/Toast/context/ToastContext';

import {formatDateToMMDDYYYY} from 'shared/utils/dates/formatDate';
import { formatDateTime12Hour } from 'shared/utils/dateUtils';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import EventsView from 'module/logBook/EventsView';
import { resolveLogbookNoteDate, stripHtmlToPlainText } from 'module/logBook/utils/logbookValidation';
import {
  getEmployeeNoteDetailLocation,
  getShiftNoteDetailLocation,
} from 'module/logBook/utils/logbookNoteNavigation';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { useWorkplaceUser } from 'module/documents/utils';


const Logbook = () => {
  const history = useHistory();
  const location = useLocation();
  const { showToast } = useToastContext();
  const [salesErrors, setSalesErrors] = useState({
    dailySales: '',
    lunchSales: '',
    dinnerSales: '',
  });
  const [trafficErrors, setTrafficErrors] = useState({
    totalFootfall: '',
    lunchFootfall: '',
    dinnerFootfall: '',
  });
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [openShiftNote, setOpenShiftNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [shiftNoteContent, setShiftNoteContent] = useState('');


  const { selectedEmployee, startDate, setStartDate, endDate, setEndDate } = useDocumentContext();
  const workplace = useWorkplaceUser();
  const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1');
  const { data: usersData } = useQuery(
    ['users-workplaces', workplaceId],
    () => getEnrichedWorkplaceUsers(workplaceId),
    {
      enabled: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );
  const [selectedEmployeeOption, setSelectedEmployeeOption] = useState(null);
  const [noteError, setNoteError] = useState('');
  const [employeeError, setEmployeeError] = useState('');
  const loggedInUserFullName = workplace.data? 
    `${workplace.data.firstName || ''} ${workplace.data.lastName || ''}`.trim() || 'Unknown User' : 
    'Unknown User';

  useEffect(() => {
    // Only set startDate to today if no date is selected in header calendar
    if (!startDate) {
      const storedDate = localStorage.getItem('logbook-selected-date');
      const nextDate = storedDate || new Date().toISOString().split('T')[0];
      setStartDate(nextDate);
      if (!storedDate) {
        localStorage.setItem('logbook-selected-date', nextDate);
      }
    }
  }, [startDate, setStartDate]);

  // Handle URL date parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get('date');
    if (dateParam) {
      setStartDate(dateParam);
    }
  }, [location.search, setStartDate]);
  const [shiftNoteError, setShiftNoteError] = useState('');
  const [isSubmittingLogbook, setIsSubmittingLogbook] = useState(false);
  const [openSalesDialog, setOpenSalesDialog] = useState(false);
  const [dailySales, setDailySales] = useState('');
  const [dailyChange, setDailyChange] = useState('');
  const [lunchSales, setLunchSales] = useState('');
  const [dinnerSales, setDinnerSales] = useState('');
  const [wtdSales, setWtdSales] = useState('');
  const [wtdChange, setWtdChange] = useState('');
  const [ptdSales, setPtdSales] = useState('');
  const [ptdChange, setPtdChange] = useState('');
  const [showEventsView, setShowEventsView] = useState(false);

  const [openTrafficDialog, setOpenTrafficDialog] = useState(false);
  const [totalFootfall, setTotalFootfall] = useState('');
  const [footfallChange, setFootfallChange] = useState('');
  const [lunchFootfall, setLunchFootfall] = useState('');
  const [dinnerFootfall, setDinnerFootfall] = useState('');
  const [wtdFootfall, setWtdFootfall] = useState('');
  const [wtdFootfallChange, setWtdFootfallChange] = useState('');
  const [ptdFootfall, setPtdFootfall] = useState('');
  const [ptdFootfallChange, setPtdFootfallChange] = useState('');
  const [peakHour, setPeakHour] = useState('');
  const [avgDwellTime, setAvgDwellTime] = useState('');
  // Day parts for dynamic mapping - no filtering needed as API returns all dayparts
  const { data: dayPartsData } = useQuery(['day_parts'], getDayParts, { staleTime: Infinity });
  const orgDayParts: Array<{ partName: string }> = (dayPartsData?.member || []).map((dp: any) => ({
    partName: dp.partName
  }));
  const normalizeKey = (k: string) => (k || '').trim().toLowerCase();
  const { data: eventTypesData } = useQuery(['eventTypes'], () => fetchAllEventTypes(), {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const eventTypesMap = useMemo(() => {
    const list = eventTypesData?.member || eventTypesData?.items || [];
    const map = new Map<number, string>();
    if (Array.isArray(list)) {
      list.forEach((type: any) => {
        if (!type) return;
        const id = typeof type.id === 'string' ? parseInt(type.id, 10) : type.id;
        if (!id) return;
        const label = type.type || type.name || type.label || type.title || 'Event';
        map.set(id, label);
      });
    }
    return map;
  }, [eventTypesData]);
  //----------------------------- DELETE DIALOG BOX VARIABLES ----------------------------
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'shiftNotes' | 'employeeNotes' | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleteEmployeeNoteId, setDeleteEmployeeNoteId] = useState<number | null>(null);
  //------------------------ DELETE DIALOG BOX VARIABLES ENDS HERE ------------------------


  const isPOSIntegrationEnabled = false;

  const getSalesFromPOS = async ({ workplaceId, date }) => {
    return [
      {
        id: 999,
        workplace: { id: workplaceId, name: 'POS Branch' },
        predicted: 150,
        actual: 140,
        time_slot: date + 'T12:00:00.000Z',
        predicted_plus_events: 160,
      },
    ];
  };

	const filteredOptions = useMemo(() => {
		if (!usersData?.length) {
			return [];
		}

		return usersData.map((user, i) => {
			const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
			const displayName = fullName || user.email || 'Unknown User';
			return {
				index: i,
				value: user.id,
				email: user.email,
				label: fullName || user.email,
			};
		});
	}, [usersData]);

  //------------------------------ Get Sales and Traffic (Unified) ---------------------------
  const {
    data: dailySummaryData,
    isLoading: isDailySummaryLoading,
    error: dailySummaryError,
  } = useQuery(
    ['workplaceDailySummary', workplaceId, startDate],
    () => getWorkplaceDailySummary({ workplaceId, date: startDate }),
    {
      enabled: !!workplaceId && !!startDate,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );
  //------------------------- Get Sales and Traffic ENDS HERE -----------------------

  const buildDayRange = (dateValue?: string) => {
    const fallback = moment().startOf('day');
    const dayMoment = dateValue ? moment(dateValue) : fallback;
    const safeMoment = dayMoment.isValid() ? dayMoment : fallback;
    return {
      fromDate: safeMoment.clone().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      toDate: safeMoment.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };
  };

  //---------------------------- Get Events -------------------------
  const { data: eventsResponse, isLoading: isEventsLoading, error: eventsError } = useQuery(
    ['logbookEvents', workplaceId, startDate],
    () => fetchAllEvents({ workplaceId, startDate, pagination: false }),
    {
      enabled: !!workplaceId && !!startDate,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );
  //--------------------- Get Events ENDS HERE  -----------------------

  const getEventTypeLabel = (event: any) => {
    if (!event) return 'Event';
    if (typeof event.eventType === 'string') return event.eventType;
    if (event.typeLabel) return event.typeLabel;
    if (event.type && typeof event.type === 'object' && event.type.type) return event.type.type;

    const extractId = (value: any) => {
      if (!value) return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parts = value.split('/');
        const last = parts.pop();
        const numeric = Number(last);
        return Number.isNaN(numeric) ? null : numeric;
      }
      if (typeof value === 'object' && value['@id']) {
        const parts = String(value['@id']).split('/');
        const last = parts.pop();
        const numeric = Number(last);
        return Number.isNaN(numeric) ? null : numeric;
      }
      return null;
    };

    const typeId = extractId(event.type) ?? extractId(event.eventTypeId);
    if (typeId && eventTypesMap.has(typeId)) {
      return eventTypesMap.get(typeId) as string;
    }
    return 'Event';
  };

  const formatEventTime = (dateValue?: string, timeValue?: string) => {
    if (timeValue) {
      const format = timeValue.length === 8 ? 'HH:mm:ss' : 'HH:mm';
      const parsed = moment(timeValue, format, true);
      if (parsed.isValid()) {
        return parsed.format('HH:mm');
      }
    }

    if (dateValue) {
      const parsedDate = moment(dateValue);
      if (parsedDate.isValid()) {
        return parsedDate.format('HH:mm');
      }
    }

    return '--';
  };

  const stripHtmlTags = (html: string | undefined | null): string => stripHtmlToPlainText(html);

  const eventsList = useMemo(() => {
    const rawEvents: any[] = eventsResponse?.member || [];
    return rawEvents
      .filter((event: any) => {
        if (!event?.startDate || !startDate) return true;
        const eventDay = moment(event.startDate).format('YYYY-MM-DD');
        const targetDay = moment(startDate).format('YYYY-MM-DD');
        return eventDay === targetDay;
      })
      .map((event: any) => ({
        id: event.id,
        eventName: event.name || event.title || event.eventName || 'Untitled Event',
        eventType: getEventTypeLabel(event),
        startTime: formatEventTime(event.startDate, event.startTime),
        endTime: formatEventTime(event.endDate, event.endTime),
      }))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [eventsResponse, eventTypesMap, startDate]);

  //---------------------------- Get checklist outstanding -------------------------
  const { data: outstandingChecklistData, isLoading: isChecklistLoading, error: checklistError } = useQuery(
    ['checklistOutstanding', workplaceId, startDate],
    () => getChecklistOutstanding(startDate, workplaceId),
    {
      enabled: !!workplaceId && !!startDate,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );
  
  // Helper function to get owner name (same as Checklist module)
  const getOwnerName = (type?: string) => {
    if (type === 'workplace') return 'Workplace';
    if (type === 'organization') return 'Organization';
    return '-';
  };
  //-------------------- Get checklist outstanding ENDS HERE  ----------------------

  //------------------------- Broadcast Messages completed ----------------------
  const { data: broadcastData, isLoading: isBroadcastLoading, error: broadcastError } = useQuery(
    ['broadcastMessages', workplaceId, startDate],
    () => getBroadcastMessages({ workplaceId, date: startDate }),
    {
      enabled: !!workplaceId && !!startDate, // Enable API call when workplaceId and startDate are available
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );
  //---------------------- Broadcast Messages ENDS HERE  ------------------------

  //--------------------------------- Shift Notes -------------------------------
  const { data: shiftNotesData, isLoading: isShiftLoading, error: shiftError } = useQuery(
    ['shiftNotes', workplaceId, startDate],
    () => {
      return getShiftNotes({ workplaceId, date: startDate });
    },
    {
      enabled: !!workplaceId && !!startDate,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      staleTime: 0,
      retry: 3,
      retryDelay: 1000,
    }
  );

  //---------------------------- Shift Notes ENDS HERE  ---------------------------

    //---------------------------------- Employee Notes --------------------------------
    const { data: empNotesData, isLoading: isEmpNotesLoading, error: empNotesError } = useQuery(
      ['empNotes', workplaceId, startDate],
      () => getEmpNotes({ workplaceId, date: startDate }),
      {
        enabled: !!workplaceId && !!startDate,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        staleTime: 0,
      }
    );
    //---------------------------- Employee Notes ENDS HERE  ---------------------------

  const handleShiftNOpen = () => {
    setOpenShiftNote(true);
  };

  const handleShiftClose = () => {
    setOpenShiftNote(false);
    };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setNoteContent('');
    setSelectedEmployeeOption(null);
    setNoteError('');
    setEmployeeError('');
    setOpen(false);
  };

  const handleSaveEmpNote = async () => {
    if (isSubmittingLogbook) return;
    let hasError = false;

  if (!selectedEmployeeOption) {
    setEmployeeError('Please select an employee.');
    hasError = true;
  } else {
    setEmployeeError('');
  }

  if (!noteContent.trim()) {
    setNoteError('Note cannot be empty.');
    hasError = true;
  } else {
    setNoteError('');
  }

  if (hasError) return;
  
    const timestamp = moment().format('YYYY-MM-DD');
  
    setIsSubmittingLogbook(true);
    try {
      await postEmpNote({
        workplaceId,
        empName: selectedEmployeeOption.value, // Use the user ID instead of label
        note: noteContent,
        timestamp,
        date: resolveLogbookNoteDate(startDate),
        creatorId: workplace.data?.id,
      });
    
      // ✅ Refresh the employee notes
      await queryClient.invalidateQueries(['empNotes', workplaceId, startDate]);

      setNoteContent('');
      setSelectedEmployeeOption(null);
      handleClose();
      showToast('success', 'Employee Note added successfully!');
    } catch (error) {
      console.error('Failed to add employee note:', error);
      showToast('error', 'Failed to add Employee Note. Please try again.');
    } finally {
      setIsSubmittingLogbook(false);
    }
  };


  //----------------------------- DELETE DIALOG BOX FUNCTIONS ----------------------------
  const handleDeleteShiftNotes = async (id: number) => {  
    setDeleteType('shiftNotes');
    setDeleteIndex(id);
    setIsDeleteDialogOpen(true);
  };
  
  const handleDeleteEmployeeNote = async (id: number) => {
    setDeleteType('employeeNotes');
    setDeleteEmployeeNoteId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === 'shiftNotes' && deleteIndex !== null) {
        await deleteShiftNote(deleteIndex);
        queryClient.setQueryData(['shiftNotes', workplaceId, startDate], (old: any) =>
          old && old.member ? { 
            ...old, 
            member: old.member.filter((note) => {
              const noteId = note['@id'] ? parseInt(note['@id'].split('/').pop()) : null;
              return noteId !== deleteIndex;
            })
          } : old
        );
        showToast('success', 'Shift Note deleted successfully!');
      }
  
      if (deleteType === 'employeeNotes' && deleteEmployeeNoteId !== null) {
        await deleteEmpNote(deleteEmployeeNoteId);
        queryClient.setQueryData(['empNotes', workplaceId, startDate], (old: any) =>
          old && old.member ? { 
            ...old, 
            member: old.member.filter((note) => {
              const noteId = note['@id'] ? parseInt(note['@id'].split('/').pop()) : null;
              return noteId !== deleteEmployeeNoteId;
            })
          } : old
        );
        showToast('success', 'Employee Note deleted successfully!');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      if (deleteType === 'shiftNotes') {
        showToast('error', 'Failed to delete Shift Note. Please try again.');
      } else if (deleteType === 'employeeNotes') {
        showToast('error', 'Failed to delete Employee Note. Please try again.');
      }
    }
    setIsDeleteDialogOpen(false);
    setDeleteType(null);
    setDeleteIndex(null);
    setDeleteEmployeeNoteId(null);
  };
  //------------------------ DELETE DIALOG BOX FUNCTIONS ENDS HERE ------------------------

  const handleSaveShiftNote = async () => {
    if (isSubmittingLogbook) return;
    if (!shiftNoteContent.trim()) {
      setShiftNoteError('Shift note cannot be empty.');
      return;
    } else {
      setShiftNoteError('');
    }
  
    setIsSubmittingLogbook(true);
    try {
      await postShiftNote({
        note: shiftNoteContent,
        workplaceId,
        date: resolveLogbookNoteDate(startDate),
      });

      // ✅ Refresh the shift notes
      await queryClient.invalidateQueries(['shiftNotes', workplaceId, startDate]);

      setShiftNoteContent('');
      setShiftNoteError('');
      handleShiftClose();
      showToast('success', 'Shift Note added successfully!');
    } catch (error) {
      console.error('Failed to add shift note:', error);
      showToast('error', 'Failed to add Shift Note. Please try again.');
    } finally {
      setIsSubmittingLogbook(false);
    }
  };

  const handleSubmitTraffic = async () => {
    if (isSubmittingLogbook) return;
    const errors = {
      totalFootfall: totalFootfall !== '' ? '' : 'This field is required',
      lunchFootfall: lunchFootfall !== '' ? '' : 'This field is required',
      dinnerFootfall: dinnerFootfall !== '' ? '' : 'This field is required',
    };

    setTrafficErrors(errors);

    if (errors.totalFootfall || errors.lunchFootfall || errors.dinnerFootfall) return;

    setIsSubmittingLogbook(true);
    try {
      const partNames = orgDayParts.map((p) => normalizeKey(p.partName));
      const salesParts: Record<string, number> = {};
      const trafficParts: Record<string, number> = {};
      if (partNames.includes('breakfast')) {
        salesParts['breakfast'] = Number(dailySales) || 0;
        trafficParts['breakfast'] = 0;
      }
      if (partNames.includes('lunch')) {
        salesParts['lunch'] = Number(lunchSales) || 0;
        trafficParts['lunch'] = Number(lunchFootfall) || 0;
      }
      if (partNames.includes('dinner')) {
        salesParts['dinner'] = Number(dinnerSales) || 0;
        trafficParts['dinner'] = Number(dinnerFootfall) || 0;
      }

      await postWorkplaceDailySummary({
        workplace: `api/workplaces/${workplaceId}`,
        author: `api/users/4`,
        date: startDate,
        sales: salesParts,
        traffic: trafficParts,
      });
      
      await queryClient.invalidateQueries(['workplaceDailySummary', workplaceId, startDate]);

      // Optionally reset form
      setTotalFootfall('');
      setFootfallChange('');
      setLunchFootfall('');
      setDinnerFootfall('');
      setWtdFootfall('');
      setWtdFootfallChange('');
      setPtdFootfall('');
      setPtdFootfallChange('');
      setPeakHour('');
      setAvgDwellTime('');
      setTrafficErrors({
        totalFootfall: '',
        lunchFootfall: '',
        dinnerFootfall: '',
      });
      // Close dialog
      setOpenTrafficDialog(false);
      showToast('success', 'Traffic data added successfully!');
    } catch (error) {
      console.error('Failed to add traffic data:', error);
      showToast('error', 'Failed to add traffic data. Please try again.');
    } finally {
      setIsSubmittingLogbook(false);
    }
  };

  const handleSubmitSales = async () => {
    if (isSubmittingLogbook) return;
    const errors = {
      dailySales: dailySales !== '' ? '' : 'This field is required',
      lunchSales: lunchSales !== '' ? '' : 'This field is required',
      dinnerSales: dinnerSales !== '' ? '' : 'This field is required',
    };

    setSalesErrors(errors);

    if (errors.dailySales || errors.lunchSales || errors.dinnerSales) return;

    setIsSubmittingLogbook(true);
    try {
      // Build dynamic sales map from organization day parts
      const partNames = orgDayParts.map((p) => normalizeKey(p.partName));
      const salesParts: Record<string, number> = {};
      const trafficParts: Record<string, number> = {};

      if (partNames.includes('breakfast')) {
        salesParts['breakfast'] = Number(dailySales) || 0; // if breakfast present and separate inputs absent
        trafficParts['breakfast'] = 0;
      }
      if (partNames.includes('lunch')) {
        salesParts['lunch'] = Number(lunchSales) || 0;
        trafficParts['lunch'] = 0;
      }
      if (partNames.includes('dinner')) {
        salesParts['dinner'] = Number(dinnerSales) || 0;
        trafficParts['dinner'] = 0;
      }

      await postWorkplaceDailySummary({
        workplace: `api/workplaces/${workplaceId}`,
        author: `api/users/4`,
        date: startDate,
        sales: salesParts,
        traffic: trafficParts,
      });
    
      await queryClient.invalidateQueries(['workplaceDailySummary', workplaceId, startDate]);
    
      // Optionally reset form
      setDailySales('');
      setDailyChange('');
      setLunchSales('');
      setDinnerSales('');
      setWtdSales('');
      setWtdChange('');
      setPtdSales('');
      setPtdChange('');
      setSalesErrors({
        dailySales: '',
        lunchSales: '',
        dinnerSales: '',
      });
      // Close dialog
      setOpenSalesDialog(false);
      showToast('success', 'Sales data added successfully!');
    } catch (error) {
      console.error('Failed to add sales data:', error);
      showToast('error', 'Failed to add sales data. Please try again.');
    } finally {
      setIsSubmittingLogbook(false);
    }
  };
  
  const getChangeArrow = (value) => {
  
    const arrowStyle = {
      width: '1em', // reserve space for the arrow
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
    };
  
    if (value > 0) {
      return (
        <span style={{ color: 'green', display: 'inline-flex', alignItems: 'center' }}>
          <span style={arrowStyle}><ArrowDropUpIcon fontSize="small" /></span>
          {value.toFixed(1)}%
        </span>
      );
    } else if (value < 0) {
      return (
        <span style={{ color: 'red', display: 'inline-flex', alignItems: 'center' }}>
          <span style={arrowStyle}><ArrowDropDownIcon fontSize="small" /></span>
          {value.toFixed(1)}%
        </span>
      );
    } else {
      // Add transparent placeholder to keep alignment
      return (
        <span style={{ color: 'gray', display: 'inline-flex', alignItems: 'center' }}>
          <span style={arrowStyle}>&nbsp;</span>
          {value.toFixed(1)}%
        </span>
      );
    }
  };

  // Permission checks - module-level Logbook permission
  const { hasModuleViewPermission, hasModuleEditPermission, hasModuleDeletePermission } = usePermissionCheck();
  const canViewLogbook = hasModuleViewPermission('Logbook');
  const canEditLogbook = hasModuleEditPermission('Logbook');
  const canDeleteLogbook = hasModuleDeletePermission('Logbook');

  const truncateText = (text: string, maxLength: number = 30): string => {
    if (!text) return '';
    const clean = String(text);
    return clean.length > maxLength ? clean.slice(0, maxLength) + '...' : clean;
  };

  const stripHtml = (html: string): string => stripHtmlToPlainText(html);

  const handleOpenAddEditSalesTraffic = async () => {
    try {
      await queryClient.fetchQuery(
        ['dayparts-union-metrics', workplaceId],
        () => getDayPartsUnionMetrics(workplaceId),
        { staleTime: 0 }
      );
    } catch (error) {
      // Navigation should not be blocked if prefetch fails.
      console.error('Failed to refresh dayparts-union-metrics before navigation:', error);
    } finally {
      history.push(ROUTES.ADD_SALES_TRAFFIC_NOTES);
    }
  };

  return (
    <>
      {showEventsView ? (
        <EventsView
          events={eventsList}
          onBack={() => setShowEventsView(false)}
        />
      ) : (
        <div className={styles.dashboardContainer}>
          {/* <h1>Hello world</h1> */}
          {/* <img src={LogbookWordIcon} className='dashboard-image' alt="" /> */}

          {/* <div className="image-container">
            <img src={LogbookWordIcon} style={{
              width: "150px"
            }} alt="" />
          </div> */}
          <div className={styles.topRow}>

          <div className={`${styles.card} ${styles.whiteCard}`}>
          <div className={styles.notesHeader} style={{ backgroundColor: '#fff' }}>
          <div>
          <Typography variant="h6" sx={{ p: 0.7 }}>
            Sales
          </Typography>
          </div>
          {!isPOSIntegrationEnabled && canEditLogbook && (
            <div className={styles.buttonsContainer}>
              <button className={styles.button} onClick={handleOpenAddEditSalesTraffic}>Add/Edit</button>
            </div>
          )}
        </div>
            {!canViewLogbook ? (
              <Box sx={{ p: 2, minHeight: 120 }}>
                <Typography variant="body2" color="textSecondary" align="center">You do not have permissions to view Sales.</Typography>
              </Box>
            ) : isDailySummaryLoading ? (
               <Box sx={{ 
                 display: 'flex', 
                 alignItems: 'center', 
                 justifyContent: 'center', 
                 backgroundColor: '#f8f9fa',
                 minHeight: '200px',
                 padding: '40px 20px',
               }}>
                 <CircularProgress />
               </Box>
            ) : dailySummaryError ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
              }}>
                <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                  Error fetching sales/traffic
                </Typography>
              </Box>
            ) : dailySummaryData?.member?.length > 0 ? (
              <Box className={styles.salesSection}>
                <TableContainer
                className={styles.cardCompleted}
                component={Paper}
                sx={{ boxShadow: 'none' }}
            >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.tableHeaderCell}>Daypart</TableCell>
                      <TableCell className={styles.tableHeaderCell}>$</TableCell>
                      <TableCell className={styles.tableHeaderCell}>Versus Last Week</TableCell>
                    </TableRow>
                  </TableHead>
          
                  <TableBody>
                  {(dailySummaryData?.member || []).map((item: any, index: number) => {
                    const formatCurrency = (value: number) =>
                      new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(value);

                    return (
                      <React.Fragment key={item.id}>
                        {/* Divider between records — skip for the first one */}
                        {index > 0 && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ p: 0 }}>
                              <Box sx={{ height: '1px', backgroundColor: '#333', my: 1 }} />
                            </TableCell>
                          </TableRow>
                        )}

                        {(() => {
                          const salesMap = item?.sales || {};
                          const changeMap = item?.salesChangePercent || {};
                          const total = Object.values(salesMap).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
                          const orderedKeys = (orgDayParts?.map((p: any) => (p?.partName || '').toString()) || [])
                            .filter((k: string) => Object.prototype.hasOwnProperty.call(salesMap, k.toLowerCase()) || Object.prototype.hasOwnProperty.call(salesMap, k))
                            .map((k: string) => Object.prototype.hasOwnProperty.call(salesMap, k) ? k : k.toLowerCase());
                          const remainingKeys = Object.keys(salesMap).filter(k => !orderedKeys.includes(k));
                          const keys = [...orderedKeys, ...remainingKeys];

                          return (
                            <>
                              {keys.map((key: string) => (
                                <TableRow key={`sales-${item.id}-${key}`}>
                                  <TableCell>{key.charAt(0).toUpperCase() + key.slice(1)}</TableCell>
                                  <TableCell>
                                    {formatCurrency(Number(salesMap[key]) || 0)}
                                  </TableCell>
                                  <TableCell>{getChangeArrow(Number(changeMap?.[key] || 0))}</TableCell>
                                </TableRow>
                              ))}

                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Sale</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{formatCurrency(total)}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{getChangeArrow(Number(changeMap?.totalsale || 0))}</TableCell>
                              </TableRow>
                            </>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
                </Table>
              </TableContainer>
            </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
              }}>
                <TableContainer
                  className={styles.cardCompleted}
                  component={Paper}
                  sx={{ boxShadow: 'none', width: '100%' }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.tableHeaderCell}>Daypart</TableCell>
                        <TableCell className={styles.tableHeaderCell}>$</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Versus Last Week</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 6, color: '#6c757d', fontSize: '14px', border: 'none' }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </div>

          <div className={`${styles.card} ${styles.whiteCard}`} style={{ display: 'flex', flexDirection: 'column' }}>
            <div className={styles.notesHeader} style={{ backgroundColor: '#fff' }}>
              <div>
                <Typography variant="h6" sx={{ p: 0.7 }}>
                  Traffic
                </Typography>
              </div>
              {!isPOSIntegrationEnabled && canEditLogbook && (
                <div className={styles.buttonsContainer}>
                  <button className={styles.button} onClick={handleOpenAddEditSalesTraffic}>Add/Edit</button>
                </div>
              )}
            </div>
            {!canViewLogbook ? (
              <Box sx={{ p: 2, minHeight: 120 }}>
                <Typography variant="body2" color="textSecondary" align="center">You do not have permissions to view Traffic.</Typography>
              </Box>
            ) : (
            /* Traffic content below */
            isDailySummaryLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
              }}>
                <CircularProgress />
              </Box>
            ) : dailySummaryError ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
              }}>
                <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                  Error fetching sales/traffic
                </Typography>
              </Box>
            ) : dailySummaryData?.member?.length > 0 ? (
              <Box className={styles.trafficSection}>
                <TableContainer
                className={styles.cardCompleted}
                component={Paper}
                sx={{ boxShadow: 'none', height: '100%' }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.tableHeaderCell}>Daypart</TableCell>
                      <TableCell className={styles.tableHeaderCell}>Count</TableCell>
                      <TableCell className={styles.tableHeaderCell} sx={{ textAlign: 'center' }}>Versus Last Week</TableCell>
                    </TableRow>
                  </TableHead>
          
                  <TableBody>
                    {Array.isArray(dailySummaryData?.member) && dailySummaryData.member.length > 0 ? (
                      dailySummaryData.member.map((item, index) => (
                      <React.Fragment key={item.id}>
                        {/* Divider row between records (skip first) */}
                        {index > 0 && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ p: 0 }}>
                              <Box sx={{ height: '1px', backgroundColor: '#333', my: 1 }} />
                            </TableCell>
                          </TableRow>
                        )}
          
                        {(() => {
                          const trafficMap = item?.traffic || {};
                          const changeMap = item?.trafficChangePercent || {};
                          const total = Object.values(trafficMap).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
                          const orderedKeys = (orgDayParts?.map((p: any) => (p?.partName || '').toString().toLowerCase()) || [])
                            .filter((k: string) => Object.prototype.hasOwnProperty.call(trafficMap, k));
                          const remainingKeys = Object.keys(trafficMap).filter(k => !orderedKeys.includes(k));
                          const keys = [...orderedKeys, ...remainingKeys];

                          return (
                            <>
                              {keys.map((key: string) => (
                                <TableRow key={`traffic-${item.id}-${key}`}>
                                  <TableCell>{key.charAt(0).toUpperCase() + key.slice(1)}</TableCell>
                                  <TableCell>
                                    {Number(trafficMap[key]) || 0}
                                  </TableCell>
                                  <TableCell>{getChangeArrow(Number(changeMap?.[key] || 0))}</TableCell>
                                </TableRow>
                              ))}

                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Customer</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  {total}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{getChangeArrow(Number(changeMap?.totalsale || 0))}</TableCell>
                              </TableRow>
                            </>
                          );
                        })()}
                      </React.Fragment>
                    ))
                    ) : (
                      <TableRow>
                      <TableCell>No traffic data available.</TableCell>
                    </TableRow>
                  )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
              }}>
                <TableContainer
                  className={styles.cardCompleted}
                  component={Paper}
                  sx={{ boxShadow: 'none', width: '100%' }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.tableHeaderCell}>Daypart</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Count</TableCell>
                        <TableCell className={styles.tableHeaderCell} sx={{ textAlign: 'center' }}>Versus Last Week</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 6, color: '#6c757d', fontSize: '14px', border: 'none' }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )
            )}
          </div>

          <div className={`${styles.card} ${styles.whiteCard}`} style={{ display: 'flex', flexDirection: 'column' }}>
            <div className={styles.notesHeader} style={{ 
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 16px',
              // add subtle grey bottom line
            }}>
              <Typography variant="h6" sx={{ p: 1 }}>
                Events
              </Typography>
            </div>
            {!canViewLogbook ? (
              <Box sx={{ p: 2, minHeight: 120 }}>
                <Typography variant="body2" color="textSecondary" align="center">You do not have permissions to view Events.</Typography>
              </Box>
            ) : isEventsLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
              }}>
                <CircularProgress />
              </Box>
            ) : eventsError ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
              }}>
                <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                  Error fetching events
                </Typography>
              </Box>
            ) : eventsList.length > 0 ? (
            <Box className={styles.eventsSection}>
              <TableContainer
                className={styles.cardStats}
                component={Paper}
                sx={{ boxShadow: 'none' }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.tableHeaderCell}>Name</TableCell>
                      <TableCell className={styles.tableHeaderCell}>Type</TableCell>
                      <TableCell className={styles.tableHeaderCell}>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eventsList.map((event: any) => (
                      <TableRow key={event.id} 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setShowEventsView(true)}
                      >
                        <TableCell>{event.eventName}</TableCell>
                        <TableCell>{event.eventType}</TableCell>
                        <TableCell>{event.startTime} – {event.endTime}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'flex-start', 
                justifyContent: 'flex-start', 
                // minHeight: '200px',
                backgroundColor: '#f8f9fa',
              }}>
                <TableContainer
                  className={styles.cardStats}
                  component={Paper}
                  sx={{ boxShadow: 'none', width: '100%' }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.tableHeaderCell}>Name</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Type</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 6, color: '#6c757d', fontSize: '14px', border: 'none' }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </div>

          </div>

          <div className={styles.middleRow}>
            <div className={`${styles.card} ${styles.whiteCard}`} style={{ height: '285px', display: 'flex', flexDirection: 'column' }}>
              <div className={styles.notesHeader} style={{ backgroundColor: '#fff' }}>
                <div>
                  <Typography variant="h6" sx={{ p: 0.7 }}>
                    Shift Notes
                  </Typography>
                </div>
                {canEditLogbook && (
                  <div className={styles.buttonsContainer}>
                    <NavLink to={ROUTES.ADD_SHIFT_NOTES}>
                      <button className={styles.button}>Add</button>
                    </NavLink>
                  </div>
                )}
              </div>
              {/* <div className={styles.notesContent}>
                Name - Entry<br />...
              </div>
              <div className={styles.notesContent}>
                Name - Entry<br />...
              </div> */}

              {!canViewLogbook ? (
                <Box sx={{ p: 2, minHeight: 160 }}>
                  <Typography variant="body2" color="textSecondary" align="center">You do not have permissions to view Shift Notes.</Typography>
                </Box>
              ) : isShiftLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  backgroundColor: '#f8f9fa',
                  minHeight: '200px',
                  padding: '40px 20px',
                  flexGrow: 1,
                }}>
                  <CircularProgress />
                </Box>
              ) : shiftError ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  backgroundColor: '#f8f9fa',
                  minHeight: '200px',
                  padding: '40px 20px',
                  flexGrow: 1,
                }}>
                  <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                    Error fetching shift notes: {shiftError instanceof Error ? shiftError.message : 'Unknown error'}
                    {shiftError && typeof shiftError === 'object' && 'status' in shiftError && ` (Status: ${(shiftError as any).status})`}
                  </Typography>
                </Box>
              ) : shiftNotesData?.member && Array.isArray(shiftNotesData.member) && shiftNotesData.member.length > 0 ? (
                <TableContainer sx={{ flexGrow: 1, maxHeight: '240px', overflowY: 'auto' }} className={styles.cardStats}>
                   <Table stickyHeader>
                     <TableHead>
                       <TableRow>
                         <TableCell className={styles.tableHeaderCell}>Author</TableCell>
                         <TableCell className={styles.tableHeaderCell}>Note</TableCell>
                         <TableCell className={styles.tableHeaderCell}>Date</TableCell>
                         <TableCell className={styles.tableHeaderCellActions}>Actions</TableCell>
                       </TableRow>
                     </TableHead>
                     <TableBody>
                       {shiftNotesData.member.map((note: any, index: number) => {
                          // Extract ID from @id field (e.g., "/api/shift_notes/5" -> 5)
                          const noteId = note['@id'] ? parseInt(note['@id'].split('/').pop()) : null;
                          
                          if (!note || !noteId || !note.date) {
                            return null;
                          }
                          const noteDate = new Date(note.date);
                          const oneWeekAgo = new Date();
                          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                          const isWithinAWeek = noteDate >= oneWeekAgo;

                          return (
                            <TableRow key={noteId || index}>
                              <TableCell className={styles.tableCell}>
                                {note.author ? 
                                  (typeof note.author === 'object' && note.author.firstName && note.author.lastName) 
                                    ? `${note.author.firstName} ${note.author.lastName}`.trim()
                                    : (typeof note.author === 'object' && note.author.firstName) 
                                      ? note.author.firstName
                                      : (typeof note.author === 'string' && note.author.includes('/')) 
                                        ? `User ${note.author.split('/').pop()}` 
                                        : (typeof note.author === 'string') 
                                          ? note.author 
                                          : 'Unknown'
                                : 'Unknown'}
                              </TableCell>
                              <TableCell 
                                className={styles.tableCell} 
                                onClick={() =>
                                  history.push(
                                    getShiftNoteDetailLocation(noteId, {
                                      canEdit: canEditLogbook,
                                      isEditableWindow: isWithinAWeek,
                                    }).pathname,
                                  )
                                }
                                style={{ cursor: 'pointer' }}
                              > 
                                {truncateText(stripHtml(note.note || ''), 30)}
                              </TableCell>
                              <TableCell className={styles.tableCell}>{formatDateToMMDDYYYY(new Date(note.date))}</TableCell>
                              <TableCell align="center" className={styles.tableCell}>
                                <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                                  {canViewLogbook && (
                                    <button
                                      className={styles.iconButton}
                                      onClick={() => history.push(ROUTES.VIEW_SHIFT_NOTES.replace(':noteId', noteId.toString()))}
                                      title="View Shift Note"
                                    >
                                      <Visibility className={styles.editIcon} />
                                    </button>
                                  )}
                                  {isWithinAWeek && canEditLogbook && (
                                    <NavLink
                                      to={ROUTES.EDIT_SHIFT_NOTES.replace(':noteId', noteId.toString())}
                                      className={styles.iconButton}
                                      style={{ textDecoration: 'none' }}
                                    >
                                      <Edit className={styles.editIcon} />
                                    </NavLink>
                                  )}
                                  {isWithinAWeek && canDeleteLogbook && (
                                    <button
                                      onClick={() => handleDeleteShiftNotes(noteId)}
                                      style={{ padding: '5px', color: 'red', background: 'transparent', border: 'none', cursor: 'pointer' }}
                                    >
                                      <Delete style={{ fontSize: '20px' }} />
                                    </button>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                     </TableBody>
                   </Table>
                 </TableContainer>
               ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    backgroundColor: '#f8f9fa',
                  }}>
                    <TableContainer
                      sx={{ flexGrow: 1, width: '100%' }}
                      className={styles.cardStats}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell className={styles.tableHeaderCell}>Author</TableCell>
                            <TableCell className={styles.tableHeaderCell}>Note</TableCell>
                            <TableCell className={styles.tableHeaderCell}>Date</TableCell>
                            <TableCell className={styles.tableHeaderCellActions}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 6, color: '#6c757d', fontSize: '14px', border: 'none' }}>
                              No records found
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
            </div>

            <div className={`${styles.card} ${styles.whiteCard}`} style={{ height: '285px', display: 'flex', flexDirection: 'column' }}>
              <div className={styles.notesHeader} style={{ backgroundColor: '#fff' }}>
                <div>
                  <Typography variant="h6" sx={{ p: 0.7 }}>
                    Employee Notes
                  </Typography>
                </div>
                {canEditLogbook && (
                  <div className={styles.buttonsContainer}>
                    <NavLink to={ROUTES.ADD_EMPLOYEE_NOTES}>
                      <button className={styles.button}>Add</button>
                    </NavLink>
                  </div>
                )}
              </div>
              {!canViewLogbook ? (
                <Box sx={{ p: 2, minHeight: 160 }}>
                  <Typography variant="body2" color="textSecondary" align="center">You do not have permissions to view Employee Notes.</Typography>
                </Box>
              ) : isEmpNotesLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  backgroundColor: '#f8f9fa',
                  minHeight: '200px',
                  padding: '40px 20px',
                  flexGrow: 1,
                }}>
                  <CircularProgress />
                </Box>
              ) : empNotesError ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  backgroundColor: '#f8f9fa',
                  minHeight: '200px',
                  padding: '40px 20px',
                  flexGrow: 1,
                }}>
                  <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                    Error fetching emp notes
                  </Typography>
                </Box>
              ) : empNotesData?.member && Array.isArray(empNotesData.member) && empNotesData.member.length > 0 ? (
                <TableContainer component={Paper} sx={{ flexGrow: 1, maxHeight: '240px', overflowY: 'auto' }} className={styles.cardStats}>
                  <Table stickyHeader className={styles.employeeNotesTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={`${styles.tableHeaderCell} ${styles.empNotesColEmployee}`}>Employee</TableCell>
                        <TableCell className={`${styles.tableHeaderCell} ${styles.empNotesColNote}`}>Note</TableCell>
                        <TableCell className={`${styles.tableHeaderCell} ${styles.empNotesColDate}`}>Date</TableCell>
                        <TableCell className={`${styles.tableHeaderCellActions} ${styles.empNotesColActions}`}>Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {empNotesData.member.map((note: any) => {
                        // Extract ID from @id field (e.g., "/api/employee_notes/6" -> 6)
                        const noteId = note['@id'] ? parseInt(note['@id'].split('/').pop()) : null;
                        
                        if (!note || !noteId || !note.date) {
                          return null;
                        }
                        
                        const noteDate = new Date(note.date);
                        const oneWeekAgo = new Date();
                        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                        const isWithinAWeek = noteDate >= oneWeekAgo;

                        return (
                          <TableRow key={noteId}>
                            <TableCell className={`${styles.tableCell} ${styles.empNotesColEmployee} ${styles.empNotesCellTruncate}`}>
                              {note.author ? 
                                (typeof note.author === 'object' && note.author.firstName && note.author.lastName) 
                                  ? `${note.author.firstName} ${note.author.lastName}`.trim()
                                  : (typeof note.author === 'object' && note.author.firstName) 
                                    ? note.author.firstName
                                    : (typeof note.author === 'string' && note.author.includes('/')) 
                                      ? `User ${note.author.split('/').pop()}` 
                                      : (typeof note.author === 'string') 
                                        ? note.author 
                                        : 'Unknown'
                              : 'Unknown'}
                            </TableCell>
                            <TableCell 
                              className={`${styles.tableCell} ${styles.empNotesColNote} ${styles.empNotesCellTruncate}`}
                              onClick={() =>
                                history.push(
                                  getEmployeeNoteDetailLocation(noteId, {
                                    canEdit: canEditLogbook,
                                    isEditableWindow: isWithinAWeek,
                                  }).pathname,
                                )
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              {truncateText(stripHtmlToPlainText(note.note), 30)}
                            </TableCell>
                            <TableCell className={`${styles.tableCell} ${styles.empNotesColDate} ${styles.empNotesCellTruncate}`}>
                              {formatDateToMMDDYYYY(noteDate)}
                            </TableCell>
                            <TableCell align="center" className={`${styles.tableCell} ${styles.empNotesColActions}`}>
                              <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                                {canViewLogbook && (
                                  <button
                                    className={styles.iconButton}
                                    onClick={() => history.push(ROUTES.VIEW_EMPLOYEE_NOTES.replace(':noteId', noteId.toString()))}
                                    title="View Employee Note"
                                  >
                                    <Visibility className={styles.editIcon} />
                                  </button>
                                )}
                                {isWithinAWeek && canEditLogbook && (
                                  <NavLink
                                    to={ROUTES.EDIT_EMPLOYEE_NOTES.replace(':noteId', noteId.toString())}
                                    className={styles.iconButton}
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <Edit className={styles.editIcon} />
                                  </NavLink>
                                )}
                                {isWithinAWeek && canDeleteLogbook && (
                                  <button
                                    onClick={() => handleDeleteEmployeeNote(noteId)}
                                    style={{
                                      padding: '5px',
                                      color: 'red',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <Delete style={{ fontSize: '20px' }} />
                                  </button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                 <Box                  sx={{ 
                   display: 'flex', 
                   flexDirection: 'column', 
                   alignItems: 'center', 
                   justifyContent: 'center', 
                   backgroundColor: '#f8f9fa',
                 }}>
                   <TableContainer
                     component={Paper}
                     className={styles.cardStats}
                     sx={{ boxShadow: 'none', width: '100%' }}
                   >
                     <Table stickyHeader className={styles.employeeNotesTable}>
                       <TableHead>
                         <TableRow>
                           <TableCell className={`${styles.tableHeaderCell} ${styles.empNotesColEmployee}`}>Employee</TableCell>
                           <TableCell className={`${styles.tableHeaderCell} ${styles.empNotesColNote}`}>Note</TableCell>
                           <TableCell className={`${styles.tableHeaderCell} ${styles.empNotesColDate}`}>Date</TableCell>
                           <TableCell className={`${styles.tableHeaderCellActions} ${styles.empNotesColActions}`}>Actions</TableCell>
                         </TableRow>
                       </TableHead>
                       <TableBody>
                         <TableRow>
                           <TableCell colSpan={4} sx={{ textAlign: 'center', py: 6, color: '#6c757d', fontSize: '14px', border: 'none' }}>
                             No records found
                           </TableCell>
                         </TableRow>
                       </TableBody>
                     </Table>
                   </TableContainer>
                 </Box>
               )}
              <div className={styles.noteHint}>
                
              </div>
            </div>
          </div>

          <div className={styles.bottomRow}>
          <div
            className={`${styles.card} ${styles.whiteCard}`}
            style={{ height: 285, display: 'flex', flexDirection: 'column' }}
          >

            <div className={styles.notesHeader} style={{ backgroundColor: '#fff' }}>
              <Typography variant="h6" sx={{ p: 0.7 }}>
                Checklist Outstanding
              </Typography>
            </div>

            {!canViewLogbook ? (
              <Box sx={{ p: 2, minHeight: 160 }}>
                <Typography variant="body2" color="textSecondary" align="center">You do not have permissions to view Checklist Outstanding.</Typography>
              </Box>
            ) : isChecklistLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
                flexGrow: 1,
              }}>
                <CircularProgress />
              </Box>
            ) : checklistError ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
                flexGrow: 1,
              }}>
                <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                  Error fetching checklist data
                </Typography>
              </Box>
            ) : Array.isArray(outstandingChecklistData?.member) && outstandingChecklistData.member.length > 0 ? (
              <TableContainer
                component={Paper}
                className={styles.cardStats}
                sx={{ 
                  flexGrow: 1, 
                  height: '100%', 
                  maxHeight: '100%',
                  boxShadow: 'none',
                  overflow: 'auto'
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.tableHeaderCell}>
                        Checklist Name
                      </TableCell>
                      <TableCell className={styles.tableHeaderCell}>
                        Assigned
                      </TableCell>
                      <TableCell className={styles.tableHeaderCell}>
                        Owner
                      </TableCell>
                      <TableCell className={styles.tableHeaderCell}>
                        Checklist Due
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(outstandingChecklistData.member as any[]).map((entry: any) => {
                      const name = entry?.checklistTemplate?.label || '-';
                      const assignee = entry?.checklistSchedule?.assignee
                        ? `${entry.checklistSchedule.assignee.firstName || ''} ${entry.checklistSchedule.assignee.lastName || ''}`.trim()
                        : entry?.checklistSchedule?.group?.label || '-';
                      
                      // Owner logic (same as Checklist module - use getOwnerName function)
                      const owner = getOwnerName(entry?.checklistTemplate?.type);
                      
                      // Format scheduled date
                      const scheduledDate = entry?.scheduledDateFormatted 
                        ? formatDateToMMDDYYYY(new Date(entry.scheduledDateFormatted + 'T00:00:00'))
                        : entry?.scheduledDate 
                          ? formatDateToMMDDYYYY(new Date(entry.scheduledDate))
                          : '-';
                      return (
                        <TableRow key={entry.id}>
                          <TableCell className={styles.tableCell}>{name}</TableCell>
                          <TableCell className={styles.tableCell}>{assignee}</TableCell>
                          <TableCell className={styles.tableCell}>{owner}</TableCell>
                          <TableCell className={styles.tableCell}>{scheduledDate}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '200px',
                backgroundColor: '#f8f9fa',
              }}>
                <TableContainer
                  component={Paper}
                  className={styles.cardStats}
                  sx={{ boxShadow: 'none', width: '100%' }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.tableHeaderCell}>Checklist Name</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Assigned</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Owner</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Checklist Due</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 6, color: '#6c757d', fontSize: '14px', border: 'none' }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </div>

          <div
            className={`${styles.card} ${styles.whiteCard}`}
            style={{ height: 285, display: 'flex', flexDirection: 'column' }}
          >
            <div className={styles.notesHeader} style={{ backgroundColor: '#fff' }}>
              <Typography variant="h6" sx={{ p: 0.7 }}>
                Broadcast Messages
              </Typography>
            </div>
            {!canViewLogbook ? (
              <Box sx={{ p: 2, minHeight: 160 }}>
                <Typography variant="body2" color="textSecondary" align="center">You do not have permissions to view Broadcast Messages.</Typography>
              </Box>
            ) : isBroadcastLoading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
                flexGrow: 1,
              }}>
                <CircularProgress />
              </Box>
            ) : broadcastError ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#f8f9fa',
                minHeight: '200px',
                padding: '40px 20px',
                flexGrow: 1,
              }}>
                <Typography variant="body2" color="error" sx={{ fontSize: '14px' }}>
                  Error fetching broadcast data
                </Typography>
              </Box>
            ) : Array.isArray(broadcastData?.items) && broadcastData.items.length > 0 ? (
              <TableContainer
                component={Paper}
                className={styles.cardStats}
                sx={{ flexGrow: 1, height: '100%', boxShadow: 'none' }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={styles.tableHeaderCell}>
                        From
                      </TableCell>
                      <TableCell className={styles.tableHeaderCell}>
                        To
                      </TableCell>
                      <TableCell className={styles.tableHeaderCell}>
                        Subject
                      </TableCell>
                      <TableCell className={styles.tableHeaderCell} colSpan={2}>
                        Date & Time
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {broadcastData.items.map((msg: any) => {
                      // Use new API response structure
                      // From: sender.firstName, sender.lastName
                      const sender = msg.sender
                        ? `${msg.sender.firstName || ''} ${msg.sender.middleName || ''} ${msg.sender.lastName || ''}`.trim() || 'Unknown'
                        : 'Unknown';
                      
                      // To: receiverGroup.groupName or receiverUser name
                      let receiver = 'Unknown';
                      if (msg.receiverGroup) {
                        receiver = msg.receiverGroup.groupName || 'Unknown';
                      } else if (msg.receiverUser) {
                        receiver = `${msg.receiverUser.firstName || ''} ${msg.receiverUser.lastName || ''}`.trim() || 'Unknown';
                      }
                      
                      // Subject: title
                      const subject = msg.title || 'No subject';
                      
                      // Date and Time: createdDate and createdTime - format as MM/DD/YYYY HH:MM:SS AM/PM
                      let formattedDateTime = '';
                      
                      if (msg.createdDate && msg.createdTime) {
                        formattedDateTime = formatDateTime12Hour(msg.createdDate, msg.createdTime);
                      } else if (msg.createdAt) {
                        formattedDateTime = formatDateTime12Hour(msg.createdAt, null);
                      }
                      
                      return (
                        <TableRow key={msg.id}>
                          <TableCell className={styles.tableCell}>{sender}</TableCell>
                          <TableCell className={styles.tableCell}>{receiver}</TableCell>
                          <TableCell className={styles.tableCell}>{subject}</TableCell>
                          <TableCell className={styles.tableCell} colSpan={2}>{formattedDateTime || 'N/A'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '200px',
                backgroundColor: '#f8f9fa',
              }}>
                <TableContainer
                  component={Paper}
                  className={styles.cardStats}
                  sx={{ boxShadow: 'none', width: '100%' }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.tableHeaderCell}>Author</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Message</TableCell>
                        <TableCell className={styles.tableHeaderCell}>Sent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 6, color: '#6c757d', fontSize: '14px', border: 'none' }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </div>

          </div>

          <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Add New Employee Note</DialogTitle>
            <DialogContent>
              <Box sx={{ minWidth: 500 }}> {/* <-- increase the width here */}
                {/* <Typography variant="subtitle1">Employee Note Content</Typography> */}
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  isSearchable
                  name="employees"
                  placeholder="Select Employee"
                  options={filteredOptions}
                  value={selectedEmployeeOption}
                  onChange={(option) => setSelectedEmployeeOption(option)}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: 'rgba(0,0,0,0.23)',
                      boxShadow: state.isFocused ? '0 0 0 1px #3f51b5' : 'none',
                      '&:hover': {
                        borderColor: '#3f51b5',
                      },
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999, // prevent it from clipping in modals
                    }),
                  }}
                />
                {employeeError && (
                  <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                    {employeeError}
                  </Typography>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={6} // optionally increase height too
                  variant="outlined"
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Enter note here"
                  margin="normal"
                  error={!!noteError}
                  helperText={noteError}
                />
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item>
                    <Button onClick={handleClose} color="secondary">
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button onClick={handleSaveEmpNote} color="primary" variant="contained" disabled={isSubmittingLogbook}>
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
          </Dialog>

          <Dialog open={openSalesDialog} onClose={() => setOpenSalesDialog(false)} fullWidth maxWidth="sm">
            <DialogTitle>Add Sales Data</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Daily Sales ($)" type="number" value={dailySales} onChange={(e) => setDailySales(e.target.value)} error={!!salesErrors.dailySales}
      helperText={salesErrors.dailySales} />
                {/* <TextField label="Daily Change (%)" type="number" value={dailyChange} onChange={(e) => setDailyChange(e.target.value)} /> */}
                <TextField label="Lunch Sales ($)" type="number" value={lunchSales} onChange={(e) => setLunchSales(e.target.value)}  error={!!salesErrors.lunchSales}
      helperText={salesErrors.lunchSales}/>
                <TextField label="Dinner Sales ($)" type="number" value={dinnerSales} onChange={(e) => setDinnerSales(e.target.value)} error={!!salesErrors.dinnerSales}
      helperText={salesErrors.dinnerSales} />
                {/* <TextField label="WTD Sales ($)" type="number" value={wtdSales} onChange={(e) => setWtdSales(e.target.value)} />
                <TextField label="WTD Change (%)" type="number" value={wtdChange} onChange={(e) => setWtdChange(e.target.value)} />
                <TextField label="PTD Sales ($)" type="number" value={ptdSales} onChange={(e) => setPtdSales(e.target.value)} />
                <TextField label="PTD Change (%)" type="number" value={ptdChange} onChange={(e) => setPtdChange(e.target.value)} /> */}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button onClick={() => setOpenSalesDialog(false)} sx={{ mr: 1 }}>Cancel</Button>
                  <Button variant="contained" color="primary" onClick={handleSubmitSales} disabled={isSubmittingLogbook}>Save</Button>
                </Box>
              </Box>
            </DialogContent>
          </Dialog>

          <Dialog open={openTrafficDialog} onClose={() => setOpenTrafficDialog(false)} fullWidth maxWidth="sm">
            <DialogTitle>Add Traffic Data</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Total Customer" type="number" value={totalFootfall} onChange={(e) => setTotalFootfall(e.target.value)} error={!!trafficErrors.totalFootfall}
      helperText={trafficErrors.totalFootfall} />
                {/* <TextField label="Footfall Change (%)" type="number" value={footfallChange} onChange={(e) => setFootfallChange(e.target.value)} /> */}
                <TextField label="Lunch Footfall" type="number" value={lunchFootfall} onChange={(e) => setLunchFootfall(e.target.value)}   error={!!trafficErrors.lunchFootfall}
      helperText={trafficErrors.lunchFootfall} />
                <TextField label="Dinner Footfall" type="number" value={dinnerFootfall} onChange={(e) => setDinnerFootfall(e.target.value)} error={!!trafficErrors.dinnerFootfall}
      helperText={trafficErrors.dinnerFootfall}/>
                {/* <TextField label="WTD Footfall" type="number" value={wtdFootfall} onChange={(e) => setWtdFootfall(e.target.value)} />
                <TextField label="WTD Change (%)" type="number" value={wtdFootfallChange} onChange={(e) => setWtdFootfallChange(e.target.value)} />
                <TextField label="PTD Footfall" type="number" value={ptdFootfall} onChange={(e) => setPtdFootfall(e.target.value)} />
                <TextField label="PTD Change (%)" type="number" value={ptdFootfallChange} onChange={(e) => setPtdFootfallChange(e.target.value)} />
                <TextField label="Peak Hour" value={peakHour} onChange={(e) => setPeakHour(e.target.value)} />
                <TextField label="Avg. Dwell Time (mins)" type="text" value={avgDwellTime} onChange={(e) => setAvgDwellTime(e.target.value)} /> */}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button onClick={() => setOpenTrafficDialog(false)} sx={{ mr: 1 }}>Cancel</Button>
                  <Button variant="contained" color="primary" onClick={handleSubmitTraffic} disabled={isSubmittingLogbook}>Save</Button>
                </Box>
              </Box>
            </DialogContent>
          </Dialog>


          {/* Delete Confirmation Dialog */}
          <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
              <DialogContent>
                                     <DialogTitle style={{ textAlign: 'start' }}>
                       Confirm Delete
                   </DialogTitle>
                  <div>
                      <p>
                          Are you sure you want to delete the {deleteType === 'shiftNotes' ? 'shift note' : 'employee note'}{' '}
                                                     {deleteType === 'shiftNotes' && deleteIndex !== null && (
                               <>
                                   <strong>&ldquo;{stripHtmlTags(shiftNotesData?.member?.find(c => {
                                     const noteId = c['@id'] ? parseInt(c['@id'].split('/').pop()) : null;
                                     return noteId === deleteIndex;
                                   })?.note) || 'Unknown note'}&rdquo;</strong>
                                   {' by '}
                                   <strong>
                                     {(() => {
                                       const note = shiftNotesData?.member?.find(c => {
                                         const noteId = c['@id'] ? parseInt(c['@id'].split('/').pop()) : null;
                                         return noteId === deleteIndex;
                                       });
                                       if (note?.author && typeof note.author === 'object') {
                                         return `${note.author.firstName || ''} ${note.author.lastName || ''}`.trim() || 'Unknown Author';
                                       }
                                       return 'Unknown Author';
                                     })()}
                                   </strong>
                               </>
                           )}
                          {deleteType === 'employeeNotes' && deleteEmployeeNoteId !== null && (
                              <>
                                  <strong>&ldquo;{stripHtmlTags(empNotesData?.member?.find(c => {
                                    const noteId = c['@id'] ? parseInt(c['@id'].split('/').pop()) : null;
                                    return noteId === deleteEmployeeNoteId;
                                  })?.note) || 'Not found'}&rdquo;</strong>
                              </>
                          )}? This action cannot be undone.
                      </p>
                  </div>
                                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                       <Button
                           style={{
                               color: 'black',
                               padding: '5px 15px',
                           }}
                           onClick={() => {
                               setIsDeleteDialogOpen(false);
                               setDeleteType(null);
                               setDeleteIndex(null);
                               setDeleteEmployeeNoteId(null);
                           }}
                       >
                           Cancel
                       </Button>
                       <Button
                           style={{
                               backgroundColor: 'red',
                               color: 'white',
                               padding: '5px 15px',
 
                           }}
                           onClick={handleConfirmDelete}
                       >
                           Delete
                       </Button>
                   </Box>
              </DialogContent>
          </Dialog>



          <Dialog open={openShiftNote} onClose={handleShiftClose} fullWidth maxWidth="sm">
            <DialogTitle>Add New Shift Note</DialogTitle>
            <DialogContent>
              <Box sx={{ minWidth: 500 }}> {/* <-- increase the width here */}
                {/* <Typography variant="subtitle1">Shift Note Content</Typography> */}
                <TextField
                  fullWidth
                  multiline
                  rows={6} // optionally increase height too
                  variant="outlined"
                  value={shiftNoteContent}
                  onChange={(e) => setShiftNoteContent(e.target.value)}
                  placeholder="Enter shift note here"
                  margin="normal"
                  error={!!shiftNoteError}
                  helperText={shiftNoteError}
                />
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item>
                    <Button onClick={handleShiftClose} color="secondary">
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button onClick={handleSaveShiftNote} color="primary" variant="contained" disabled={isSubmittingLogbook}>
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </>
  );
}

export default Logbook;